import { types as t } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import { observable, action, runInAction, makeObservable, IObservableArray } from 'mobx'
import fetch from '../../utils/fetch'
import errorToast from '../../utils/error-toast'
import memoize from 'lodash/memoize'
import { Params } from './clientRoutes'
import Mapping from './mapping'
import type { FieldType, JsonValue, LabelEntry } from './types'

let metadata = null
const jsonApiAction = async (url, params, headers = {}) => {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...headers
    },
    mode: 'cors',
    body: JSON.stringify(params)
  })

  if (!res.ok) {
    throw Error(res.statusText)
  }
  const data = await res.json()
  return data
}

export class JsonApiProvider {
  data: any = {}
  config
  refresher
  accessToken = ''

  constructor (config) {
    makeObservable(this, {
      data: observable,
      query: action
    })

    this.config = config
  }

  query = async () => {
    const request = this.config.mapping.toProps
    if (this.config.oAuth.withOAuth) {
      if (this.accessToken) {
        try {
          const data = await jsonApiAction('/api/jsonproxy', request, {
            Authorization: 'Bearer ' + this.accessToken
          })
          runInAction(() => (this.data = data))
        } catch (e) {
          errorToast(`Error querying ${this.config.name}: `, e)
        }
      } else {
        const { authUrl, params } = this.config.oAuth
        const authParams = params.reduce((acc, item) => {
          acc[item.name] = item.value
          return acc
        }, {})
        try {
          const access = await jsonApiAction('/api/jsonproxyauth', {
            authUrl,
            authParams
          })
          this.accessToken = access.access_token

          request.headers = {
            ...request.headers,
            Authorization: 'Bearer ' + access.access_token
          }
          if (request && request.url) {
            const data = await jsonApiAction('/api/jsonproxy', request)
            runInAction(() => (this.data = data))
          }
        } catch (e) {
          errorToast(`Error querying ${this.config.name}: `, e)
        }
      }
      return
    }
    try {
      if (!request || !request.url) return

      const res = await fetch('/api/jsonproxy', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        mode: 'cors',
        body: request && JSON.stringify(request)
      })
      if (!res.ok) {
        const r = await res.json()
        const er = JSON.parse(r.body) || { message: `Error querying ${this.config.name}` }
        throw er.message
      }

      let data
      let headers
      const resJson = await res.json()

      if (
        request.headers &&
        request.headers['Content-Type'] === 'application/pdf'
      ) {
        data = await resJson.body
        headers = await res.headers
      } else {
        data = JSON.parse(resJson.body)
        headers = resJson.headers
      }

      runInAction(() => {
        this.data = { body: data, headers } as any
      })
    } catch (e) {
      errorToast(`Error querying ${this.config.name}: `, e)
    }
    if (this.config.autoRefreshSeconds > 0) {
      if (this.refresher) clearTimeout(this.refresher)
      this.refresher = setTimeout(
        () => this.query().then(),
        this.config.autoRefreshSeconds * 1000
      )
    }
    return this.data
  }
}

const OAuth = t
  .model('OAuth', {
    withOAuth: t.optional(t.boolean, false),
    authUrl: t.maybeNull(t.string),
    params: t.optional(t.array(Params), [])
  })
  .actions(self => {
    function addItem (item = {}) {
      self.params.push({})
    }
    return {
      addItem
    }
  })
  .actions(crudActions)

export const defaultRequestMapping = {
  type: 'object',
  children: [
    { key: 'url', type: 'string' },
    { key: 'method', type: 'string' },
    { key: 'headers', type: 'object' },
    { key: 'body', type: 'string' }
  ]
}

interface JSONApiField {
  createable: boolean,
  editable: boolean,
  fieldPath: string,
  isNameField: boolean,
  label: string,
  readable: boolean,
  referenceTo: string,
  sobjectAPIName: string,
  type: FieldType
}

class JSONInstance {
  fields = [] as IObservableArray<JSONApiField> 

  constructor (config: JsonValue) {
    makeObservable(this, {
      fields: observable
    })

    const provider = new JsonApiProvider(config)
    provider.query().then(() => {
      const item = Array.isArray(provider.data) ? provider.data[0] : provider.data

      const fields = [] as IObservableArray<JSONApiField>
      for (const key in item) {
        // take only primitives
        if (Object(item[key]) !== item[key]) {
          fields.push({
            createable: false,
            editable: false,
            fieldPath: key,
            isNameField: false,
            label: key,
            readable: true,
            referenceTo: '',
            sobjectAPIName: 'JSON-API',
            type: (typeof item[key]).toUpperCase() as FieldType
          })
        }
      }
      runInAction(() => (this.fields = fields))
    })
  }
}

class JSONMetadata {
  instance: (config: JsonValue) => JSONInstance

  constructor () {
    makeObservable(this, {
      instance: observable
    })

    this.instance = memoize((config: JsonValue) => {
      return new JSONInstance(config)
    })
  }
}

export default t
  .model('JsonApi', {
    type: t.optional(t.string, 'JSON-API'),
    mapping: t.late(() => t.optional(Mapping, defaultRequestMapping)),
    paths: t.optional(t.array(t.string), []),
    autoRefreshSeconds: t.optional(t.number, 0),
    oAuth: t.optional(OAuth, {})
  })
  .views(self => ({
    get request () {
      return self.mapping.toProps || {}
    },
    get provider () {
      return JsonApiProvider
    },
    get metadata () {
      if (!(this as any)._metadata) {
        (this as any)._metadata = new JSONMetadata()
      }
      return (this as any)._metadata as JSONMetadata
    },
    getFields () {
      return this.metadata.instance(self as any).fields
    }
  }))
  .views(self => ({
    labels (): LabelEntry {
      return {
        key: (self as any).id,
        label: (self as any).name,
        childNodes: [
          {
            key: 'data',
            label: 'Data',
            isExpanded: false,
            childNodes: self.paths.map(p => ({
              key: p,
              label: p,
              isExpanded: false
            }))
          },
          {
            key: 'state',
            label: 'State',
            isExpanded: false,
            childNodes: [{
              key: 'headers',
              label: 'headers',
              secondaryLabel: 'state'
            }]
          }
        ]
      }
    }
  }))
  .actions(crudActions)
